import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import ChatPanel from '../components/ChatPanel';
import { SlaBadge } from '../components/SlaTimer';
import { toast } from 'sonner';
import {
  Shield, MapPin, Wifi, WifiOff, CheckSquare, Square,
  ChevronRight, ArrowLeft, Clock, User, Phone, Download, Mic, Loader2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Progress } from '../components/ui/progress';

export default function TechMobile() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [profile, setProfile] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [checklist, setChecklist] = useState(null);
  const [slaStatus, setSlaStatus] = useState(null);
  const [view, setView] = useState('queue'); // queue | detail | chat
  const [loading, setLoading] = useState(true);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [notifPermission, setNotifPermission] = useState(Notification?.permission || 'default');
  const [installPrompt, setInstallPrompt] = useState(null);
  const [recording, setRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [transcribing, setTranscribing] = useState(false);

  // PWA: Register service worker + capture install prompt
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js').catch(() => {});
    }
    const handler = (e) => { e.preventDefault(); setInstallPrompt(e); };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  // Online/Offline detection
  useEffect(() => {
    const goOnline = () => { setIsOnline(true); toast.success('Back online'); };
    const goOffline = () => { setIsOnline(false); toast.error('You are offline'); };
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => { window.removeEventListener('online', goOnline); window.removeEventListener('offline', goOffline); };
  }, []);

  const requestNotifications = async () => {
    if ('Notification' in window) {
      const perm = await Notification.requestPermission();
      setNotifPermission(perm);
      if (perm === 'granted') toast.success('Notifications enabled');
    }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [ticketsRes, profileRes] = await Promise.all([
        api.get('/api/tickets?limit=50'),
        api.get('/api/technicians/profile').catch(() => ({ data: null })),
      ]);
      setTickets(ticketsRes.data.tickets || []);
      setProfile(profileRes.data);
    } catch (e) { /* handled */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleStatusChange = async (ticketId, newStatus) => {
    try {
      await api.put(`/api/tickets/${ticketId}`, { status: newStatus });
      toast.success(`Status: ${newStatus.replace('_', ' ')}`);
      fetchData();
      if (selectedTicket?.id === ticketId) {
        const { data } = await api.get(`/api/tickets/${ticketId}`);
        setSelectedTicket(data);
      }
    } catch { toast.error('Update failed'); }
  };

  const handleToggleAvailability = async () => {
    const newStatus = profile?.availability === 'available' ? 'offline' : 'available';
    try {
      await api.put('/api/technicians/profile', { availability: newStatus });
      setProfile(prev => ({ ...prev, availability: newStatus }));
      toast.success(newStatus === 'available' ? 'You are now available' : 'You are offline');
    } catch { toast.error('Update failed'); }
  };

  const openTicket = async (ticket) => {
    setSelectedTicket(ticket);
    setView('detail');
    try {
      const [clRes, slaRes] = await Promise.all([
        api.get(`/api/tickets/${ticket.id}/checklist`),
        api.get(`/api/sla/status/${ticket.id}`),
      ]);
      setChecklist(clRes.data.checklist || null);
      setSlaStatus(slaRes.data || null);
    } catch (e) { /* handled */ }
  };

  const handleToggleStep = async (stepId, completed) => {
    try {
      const { data } = await api.put(`/api/tickets/${selectedTicket.id}/checklist/${stepId}`, { completed: !completed });
      setChecklist(data.checklist);
    } catch { toast.error('Update failed'); }
  };

  // ── Queue View ──────────────────────────────────────────────
  if (view === 'queue') {
    const myTickets = tickets.filter(t => t.assigned_to === user?.id);
    const openQueue = tickets.filter(t => t.status === 'new' && !t.assigned_to);

    return (
      <div className="min-h-screen bg-gray-50" data-testid="tech-mobile">
        {/* Mobile header */}
        <div className="bg-[#0F172A] text-white px-4 py-4 flex items-center justify-between safe-area-top">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#D4A843] rounded-lg flex items-center justify-center">
              <Shield className="w-4 h-4 text-white" />
            </div>
            <div>
              <p className="text-sm font-bold">TechOps</p>
              <p className="text-[10px] text-gray-400">{user?.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {!isOnline && <span className="flex items-center gap-1 text-[10px] text-red-400"><WifiOff className="w-3 h-3" /> Offline</span>}
            {isOnline && <Wifi className="w-3 h-3 text-emerald-400" />}
            {notifPermission !== 'granted' && (
              <button onClick={requestNotifications} className="text-[10px] text-amber-400 underline" data-testid="enable-notif-btn">Notifications</button>
            )}
            <div className="flex items-center gap-2">
              <span className={`w-2 h-2 rounded-full ${profile?.availability === 'available' ? 'bg-emerald-400' : 'bg-gray-500'}`} />
              <Switch
                checked={profile?.availability === 'available'}
                onCheckedChange={handleToggleAvailability}
                data-testid="mobile-availability-toggle"
              />
            </div>
            <button onClick={logout} className="text-xs text-gray-400">Logout</button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-2 p-4">
          <div className="bg-white rounded-lg p-3 text-center border border-gray-200">
            <p className="text-xl font-bold text-gray-900">{myTickets.length}</p>
            <p className="text-[10px] text-gray-500">My Jobs</p>
          </div>
          <div className="bg-white rounded-lg p-3 text-center border border-gray-200">
            <p className="text-xl font-bold text-[#D4A843]">{openQueue.length}</p>
            <p className="text-[10px] text-gray-500">Available</p>
          </div>
          <div className="bg-white rounded-lg p-3 text-center border border-gray-200">
            <p className="text-xl font-bold text-emerald-600">{profile?.total_jobs || 0}</p>
            <p className="text-[10px] text-gray-500">Completed</p>
          </div>
        </div>

        {/* PWA Install Banner */}
        {installPrompt && (
          <div className="mx-4 mb-3 bg-[#D4A843] rounded-lg p-3 flex items-center justify-between" data-testid="pwa-install-banner">
            <div className="flex items-center gap-2">
              <Download className="w-4 h-4 text-white" />
              <p className="text-xs text-white font-medium">Install TechOps on your device</p>
            </div>
            <button onClick={async () => { installPrompt.prompt(); const { outcome } = await installPrompt.userChoice; if (outcome === 'accepted') { setInstallPrompt(null); toast.success('App installed!'); } }} className="bg-white text-[#D4A843] text-xs font-bold px-3 py-1.5 rounded-lg" data-testid="pwa-install-btn">Install</button>
          </div>
        )}

        {/* My Active Jobs */}
        <div className="px-4 mb-3">
          <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wider">My Active Jobs</h2>
        </div>
        <div className="px-4 space-y-2 mb-6">
          {myTickets.filter(t => !['resolved', 'closed'].includes(t.status)).length === 0 && (
            <p className="text-sm text-gray-400 text-center py-6 bg-white rounded-lg border border-gray-200">No active jobs</p>
          )}
          {myTickets.filter(t => !['resolved', 'closed'].includes(t.status)).map(t => (
            <button
              key={t.id}
              onClick={() => openTicket(t)}
              className="w-full bg-white rounded-lg border border-gray-200 p-4 text-left active:bg-gray-50"
              data-testid={`mobile-ticket-${t.id}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">{t.title}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded badge-${t.status}`}>{t.status.replace('_', ' ')}</span>
                    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded badge-${t.priority}`}>{t.priority}</span>
                    {t.service_type === 'on_site' && <MapPin className="w-3 h-3 text-gray-400" />}
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-gray-400 shrink-0 mt-1" />
              </div>
            </button>
          ))}
        </div>

        {/* Open Queue */}
        <div className="px-4 mb-3">
          <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Open Queue</h2>
        </div>
        <div className="px-4 space-y-2 pb-8">
          {openQueue.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-6 bg-white rounded-lg border border-gray-200">No open tickets</p>
          )}
          {openQueue.map(t => (
            <button
              key={t.id}
              onClick={() => openTicket(t)}
              className="w-full bg-white rounded-lg border border-gray-200 p-4 text-left active:bg-gray-50"
              data-testid={`mobile-queue-${t.id}`}
            >
              <p className="text-sm font-semibold text-gray-900 truncate">{t.title}</p>
              <div className="flex items-center gap-2 mt-1">
                <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded badge-${t.priority}`}>{t.priority}</span>
                <span className="text-[10px] text-gray-400">{t.category}</span>
                <span className="text-[10px] text-gray-400">{t.service_type}</span>
              </div>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // ── Detail View ──────────────────────────────────────────────
  if (view === 'detail' && selectedTicket) {
    return (
      <div className="min-h-screen bg-gray-50" data-testid="tech-mobile-detail">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3 sticky top-0 z-10">
          <button onClick={() => { setView('queue'); setSelectedTicket(null); }} className="p-1">
            <ArrowLeft className="w-5 h-5 text-gray-700" />
          </button>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-gray-900 truncate">{selectedTicket.title}</p>
            <div className="flex items-center gap-2">
              <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded badge-${selectedTicket.status}`}>{selectedTicket.status.replace('_', ' ')}</span>
              <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded badge-${selectedTicket.priority}`}>{selectedTicket.priority}</span>
            </div>
          </div>
          <button onClick={() => setView('chat')} className="bg-[#D4A843] text-[#0A0A0A] font-semibold text-xs px-3 py-2 rounded-lg font-medium" data-testid="open-chat-btn">
            Chat
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* SLA */}
          {slaStatus && (
            <SlaBadge slaStatus={slaStatus} />
          )}

          {/* Quick Actions */}
          <div className="grid grid-cols-2 gap-2">
            {selectedTicket.status === 'new' && !selectedTicket.assigned_to && (
              <Button className="bg-[#D4A843] text-[#0A0A0A] font-semibold rounded-lg text-sm py-3 col-span-2" onClick={async () => {
                await api.put(`/api/tickets/${selectedTicket.id}`, { assigned_to: user?.id });
                toast.success('Job accepted');
                fetchData();
                const { data } = await api.get(`/api/tickets/${selectedTicket.id}`);
                setSelectedTicket(data);
              }} data-testid="accept-job-btn">
                Accept Job
              </Button>
            )}
            {selectedTicket.assigned_to === user?.id && (
              <>
                {selectedTicket.status === 'assigned' && (
                  <Button className="bg-amber-500 text-white rounded-lg text-sm py-3 col-span-2" onClick={() => handleStatusChange(selectedTicket.id, 'in_progress')} data-testid="start-work-btn">
                    Start Working
                  </Button>
                )}
                {selectedTicket.status === 'in_progress' && (
                  <>
                    <Button className="bg-emerald-600 text-white rounded-lg text-sm py-3" onClick={() => handleStatusChange(selectedTicket.id, 'resolved')} data-testid="resolve-btn">
                      Resolve
                    </Button>
                    <Button variant="outline" className="rounded-lg text-sm py-3" onClick={() => handleStatusChange(selectedTicket.id, 'pending_approval')} data-testid="pending-btn">
                      Need Approval
                    </Button>
                  </>
                )}
              </>
            )}
          </div>

          {/* Details */}
          <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-3">
            <p className="text-sm text-gray-600 leading-relaxed">{selectedTicket.description}</p>
            <div className="grid grid-cols-2 gap-3 pt-3 border-t border-gray-100">
              <div><p className="text-[10px] text-gray-400">Category</p><p className="text-xs font-medium">{selectedTicket.category}</p></div>
              <div><p className="text-[10px] text-gray-400">Service</p><p className="text-xs font-medium">{selectedTicket.service_type}</p></div>
              <div><p className="text-[10px] text-gray-400">Customer</p><p className="text-xs font-medium">{selectedTicket.creator_name}</p></div>
              <div><p className="text-[10px] text-gray-400">Created</p><p className="text-xs font-medium">{new Date(selectedTicket.created_at).toLocaleDateString()}</p></div>
            </div>
          </div>

          {/* Voice-to-Ticket */}
          <div className="bg-white rounded-lg border border-gray-200 p-4" data-testid="voice-to-ticket">
            <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-3 flex items-center gap-2">
              <Mic className="w-4 h-4 text-[#D4A843]" /> Voice Note
            </h3>
            <div className="flex items-center gap-3">
              <button
                onClick={async () => {
                  if (recording && mediaRecorder) {
                    mediaRecorder.stop();
                    setRecording(false);
                  } else {
                    try {
                      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
                      const mr = new MediaRecorder(stream, { mimeType: 'audio/webm' });
                      const chunks = [];
                      mr.ondataavailable = e => chunks.push(e.data);
                      mr.onstop = async () => {
                        stream.getTracks().forEach(t => t.stop());
                        const blob = new Blob(chunks, { type: 'audio/webm' });
                        setTranscribing(true);
                        try {
                          const formData = new FormData();
                          formData.append('audio', blob, 'voice_note.webm');
                          const { data } = await api.post('/api/enhance/voice/transcribe', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
                          if (data.text) {
                            const newDesc = (selectedTicket.description || '') + '\n\n[Voice Note] ' + data.text;
                            await api.put(`/api/tickets/${selectedTicket.id}`, { description: newDesc });
                            setSelectedTicket(prev => ({ ...prev, description: newDesc }));
                            toast.success('Voice note transcribed and added');
                          }
                        } catch { toast.error('Transcription failed'); }
                        finally { setTranscribing(false); }
                      };
                      mr.start();
                      setMediaRecorder(mr);
                      setRecording(true);
                      toast.info('Recording... tap again to stop');
                    } catch { toast.error('Microphone access denied'); }
                  }
                }}
                className={`w-12 h-12 rounded-full flex items-center justify-center ${recording ? 'bg-red-500 animate-pulse' : 'bg-[#D4A843]'} text-white`}
                disabled={transcribing}
                data-testid="voice-record-btn"
              >
                {transcribing ? <Loader2 className="w-5 h-5 animate-spin" /> : <Mic className="w-5 h-5" />}
              </button>
              <p className="text-xs text-gray-500">
                {transcribing ? 'Transcribing...' : recording ? 'Recording... tap to stop' : 'Tap to record a voice note'}
              </p>
            </div>
          </div>

          {/* Checklist */}
          {checklist && (
            <div className="bg-white rounded-lg border border-gray-200 p-4" data-testid="mobile-checklist">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider">{checklist.workflow_name}</h3>
                <span className="text-xs font-bold text-[#D4A843]">{checklist.progress || 0}%</span>
              </div>
              <Progress value={checklist.progress || 0} className="h-2 mb-3" />
              <div className="space-y-1">
                {(checklist.steps || []).map(step => (
                  <button
                    key={step.id}
                    onClick={() => handleToggleStep(step.id, step.completed)}
                    className={`w-full flex items-center gap-3 p-3 rounded-lg text-left active:bg-gray-100 ${step.completed ? 'bg-emerald-50' : ''}`}
                    data-testid={`mobile-step-${step.id}`}
                  >
                    {step.completed ?
                      <CheckSquare className="w-5 h-5 text-emerald-500 shrink-0" /> :
                      <Square className="w-5 h-5 text-gray-300 shrink-0" />
                    }
                    <span className={`text-sm ${step.completed ? 'text-gray-400 line-through' : 'text-gray-700'}`}>{step.title}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Chat View ────────────────────────────────────────────────
  if (view === 'chat' && selectedTicket) {
    return (
      <div className="min-h-screen bg-white flex flex-col" data-testid="tech-mobile-chat">
        <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3">
          <button onClick={() => setView('detail')} className="p-1">
            <ArrowLeft className="w-5 h-5 text-gray-700" />
          </button>
          <div>
            <p className="text-sm font-bold text-gray-900">Chat</p>
            <p className="text-[10px] text-gray-400 truncate max-w-[200px]">{selectedTicket.title}</p>
          </div>
        </div>
        <div className="flex-1">
          <ChatPanel ticketId={selectedTicket.id} />
        </div>
      </div>
    );
  }

  return null;
}
